import {
  memo,
  type ComponentPropsWithoutRef,
  type JSX,
  type ReactNode,
} from "react";

import { IconImageFileLandscape } from "@mobsuccess-devops/icons/image-file-landscape";
import { cx } from "@mobsuccess-devops/styled-system/css";
import { HStack } from "@mobsuccess-devops/styled-system/jsx";

import mobyNotFound from "../../assets/moby-not-found.png";
import { useLocales } from "../../features/localization";
import { Button } from "../Button";
import { Image } from "../Image";
import { Typo } from "../Typo";
import { Progress } from "../_PandaArk/Progress";

import { styles } from "./styles";

type DataNotFoundErrorPageProps = {
  hasFilters?: boolean;
  onClearFilters?: () => void;
  title?: ReactNode;
  description?: ReactNode;
} & ComponentPropsWithoutRef<"div">;

function DataNotFoundErrorPage({
  hasFilters,
  onClearFilters,
  title,
  description,
  ...props
}: DataNotFoundErrorPageProps): JSX.Element {
  const classes = styles();
  const { notFound } = useLocales();

  return (
    <div {...props} className={cx(classes.layout, props.className)}>
      <div className={classes.imageContainer}>
        <Image.Root>
          <Image.Img src={mobyNotFound} alt="Moby" className={classes.image} />
          <Image.Fallback borderRadius="md" size="120px" color="interface">
            <IconImageFileLandscape variant="regular" size="full" />
          </Image.Fallback>
          <Image.Loader>
            <Progress.Circular size="md" value="indeterminate" />
          </Image.Loader>
        </Image.Root>
      </div>
      <div className={classes.content}>
        <Typo.Title fontSize="32px" color="interface" className={classes.text}>
          {title ?? notFound.title}
        </Typo.Title>
        <Typo.Body color="interface" className={classes.text}>
          {description ?? notFound.description}
        </Typo.Body>
        <HStack>
          <Button
            variant="outlined"
            palette="interface"
            hidden={!hasFilters}
            onClick={onClearFilters}
          >
            {notFound.clearFilters}
          </Button>
        </HStack>
      </div>
    </div>
  );
}

const MemoizedComponent = memo(DataNotFoundErrorPage);
export { MemoizedComponent as DataNotFoundErrorPage };
